import logo from '@/assets/Orino.png';
import useRegister from '@/hooks/auth/useRegister';
import {
  EyeInvisibleOutlined,
  EyeTwoTone,
  LockOutlined,
  MailOutlined,
  ShopOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Alert, Button, Form, Input, Spin } from 'antd';
import i18n from 'i18next';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

const Register = () => {
  const { t } = useTranslation(['common']);

  const [alertMessage, setAlertMessage] = useState<{
    message: string;
    description: string;
    type: 'success' | 'error' | 'info' | 'warning';
  } | null>(null);
  const { registerUser, isPending } = useRegister();

  const onFinish = (values: {
    name: string;
    email: string;
    password: string;
    confirmPassword: string;
    businessName: string;
  }) => {
    if (values.password !== values.confirmPassword) {
      setAlertMessage({
        message: t('error'),
        description: t('passwordsDoNotMatch'),
        type: 'error',
      });
      return;
    }
    registerUser({
      name: values.name,
      email: values.email,
      password: values.password,
      businessName: values.businessName,
    }).then(() => {
      setAlertMessage({
        message: t('success'),
        description: t('registrationSuccessfulDescription'),
        type: 'success',
      });
    });
  };

  const isArabic = i18n.language === 'ar';

  return (
    <section
      className={`flex flex-col md:flex-row h-screen ${isArabic ? 'md:flex-row-reverse' : 'md:flex-row'}`}
      dir={isArabic ? 'rtl' : 'ltr'}
    >
      {/* Register Form Container */}
      <div className="flex-1 flex flex-col justify-center items-center bg-gray-100 p-4 md:p-8">
        <div className="w-full max-w-md px-4">
          <Spin spinning={isPending}>
            <h2 className="text-2xl font-bold mb-6 text-center">{t('register')}</h2>

            {alertMessage && (
              <Alert
                message={alertMessage.message}
                description={alertMessage.description}
                type={alertMessage.type}
                showIcon
                closable
                onClose={() => setAlertMessage(null)}
                className="mb-4"
              />
            )}

            {!alertMessage && (
              <Form name="register" layout="vertical" initialValues={{}} onFinish={onFinish}>
                {/* Name Field */}
                <Form.Item
                  name="name"
                  rules={[
                    {
                      required: true,
                      message: t('nameRequired'),
                    },
                    {
                      min: 2,
                      message: t('nameMinLength'),
                    },
                    {
                      pattern: /^[a-zA-Z\s]*$/,
                      message: t('nameNoSpecialChars'),
                    },
                    {
                      validator: (_, value) => {
                        if (!value) return Promise.resolve();
                        const parts = String(value).trim().split(/\s+/);
                        return parts.length >= 2
                          ? Promise.resolve()
                          : Promise.reject(
                              new Error(
                                t('firstAndLastNameRequired', {
                                  defaultValue: 'Please enter your first and last name',
                                })
                              )
                            );
                      },
                    },
                  ]}
                >
                  <Input
                    prefix={<UserOutlined className="text-gray-400" />}
                    placeholder={t('name')}
                    className="w-full"
                  />
                </Form.Item>

                {/* Email Field */}
                <Form.Item
                  name="email"
                  rules={[
                    {
                      required: true,
                      message: t('emailRequired'),
                    },
                    {
                      type: 'email',
                      message: t('invalidEmail'),
                    },
                  ]}
                >
                  <Input
                    prefix={<MailOutlined className="text-gray-400" />}
                    type="email"
                    placeholder={t('email')}
                    className="w-full"
                  />
                </Form.Item>

                <Form.Item
                  name="businessName"
                  rules={[
                    {
                      required: true,
                      message: t('businessNameRequired'),
                    },
                    {
                      min: 2,
                      message: t('businessNameMinLength'),
                    },
                    {
                      pattern: /^[a-zA-Z\s]*$/,
                      message: t('businessNameNoSpecialChars'),
                    },
                  ]}
                >
                  <Input
                    prefix={<ShopOutlined className="text-gray-400" />}
                    placeholder={t('businessName')}
                    className="w-full"
                  />
                </Form.Item>

                {/* Password Field */}
                <Form.Item
                  name="password"
                  rules={[
                    {
                      required: true,
                      message: t('passwordRequired'),
                    },
                    {
                      min: 6,
                      message: t('passwordMinLength'),
                    },
                    {
                      pattern: /^\S*$/,
                      message: t('passwordNoSpaces'),
                    },
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined className="text-gray-400" />}
                    placeholder={t('password')}
                    className="w-full"
                    iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                  />
                </Form.Item>

                {/* Confirm Password Field */}
                <Form.Item
                  name="confirmPassword"
                  dependencies={['password']}
                  rules={[
                    {
                      required: true,
                      message: t('confirmPasswordRequired'),
                    },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue('password') === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error(t('passwordsDoNotMatch')));
                      },
                    }),
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined className="text-gray-400" />}
                    placeholder={t('confirmPassword')}
                    className="w-full"
                    iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                  />
                </Form.Item>

                {/* Submit Button */}
                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    size="large"
                    className="w-full bg-primary-start hover:bg-primary-middle"
                  >
                    {t('register')}
                  </Button>
                </Form.Item>
              </Form>
            )}

            {/* Login Link */}
            <div className="text-center mt-4">
              <span className="text-gray-600">{t('haveAccount')} </span>
              <Link
                to="/login"
                className="text-primary-start hover:text-primary-middle font-semibold"
              >
                {t('loginHere')}
              </Link>
            </div>
          </Spin>
        </div>
      </div>

      {/* Welcome Section */}
      <div className="hidden md:flex flex-1 justify-center items-center bg-primary-start p-8">
        <img src={logo} alt="Orino" className="w-full h-full object-contain" />
      </div>
    </section>
  );
};

export default Register;
